var _excluded = ["iconColor", "icon", "iconSize", "value", "defaultValue", "children", "name", "sx", "stateManagerRef", "onChange", "onMenuOpen", "dataAttributes", "menuZIndex", "className"],
  _excluded2 = ["children"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useRef, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import ReactSelect, { components } from 'react-select';
import { forwardRef, omitThemingProps, tqc, useMultiStyleConfig } from '../../system';
import { split, assignRef, mergeRefs, cx, __DEV__ } from '../../utils';
import { useFormControl } from '../form-control';
import { useControllableProp } from '../../hooks';
import { layoutPropNames } from '../../styled-system';
var getValue = (options, value) => {
  for (var option of options) {
    var _option$options;
    var groupedOption = (_option$options = option.options) === null || _option$options === void 0 ? void 0 : _option$options.find(opt => opt.value === value);
    if (groupedOption) return groupedOption;
  }
  return options.find(opt => opt.value === value);
};
var isGroup = element => {
  if (! /*#__PURE__*/React.isValidElement(element)) return false;
  return element.type === 'optgroup';
};
var isValue = element => {
  if (! /*#__PURE__*/React.isValidElement(element)) return false;
  return element.type === 'option';
};
var SELECT_CLASSNAME_PREFIX = 'tqc-select';

/**
 * React component used to select one item from a list of options.
 */
export var Select = forwardRef((props, ref) => {
  var _omitThemingProps = omitThemingProps(props),
    {
      iconColor,
      icon = /*#__PURE__*/React.createElement(FaChevronDown, null),
      iconSize,
      value: valueProp,
      defaultValue,
      children,
      name,
      sx,
      stateManagerRef,
      onChange: onChangeProp = () => {},
      onMenuOpen: onMenuOpenProp = () => {},
      dataAttributes,
      menuZIndex,
      className
    } = _omitThemingProps,
    rest = _objectWithoutProperties(_omitThemingProps, _excluded);
  var styles = useMultiStyleConfig('Select', props);
  var reactSelectComponentRef = useRef(null);
  var [layoutProps, otherProps] = split(rest, layoutPropNames);
  var selectElementProps = useFormControl(otherProps);
  function createReactSelectOptions(selectChildren) {
    if (selectChildren === undefined || selectChildren === null) return [];
    var results = [];
    React.Children.forEach(selectChildren, child => {
      if ( /*#__PURE__*/React.isValidElement(child)) {
        if (isGroup(child)) {
          results.push({
            label: child.props.label,
            options: createReactSelectOptions(child.props.children)
          });
        } else if (isValue(child)) {
          return results.push(createOption(child));
        }
      }
    });
    return results;
  }
  var createOption = element => {
    return _objectSpread({
      value: element.props.value,
      label: element.props.children,
      isDisabled: element.props.disabled
    }, element.props);
  };

  /**
   * Create options array from children to pass to
   * React Select options property.
   */
  var options = createReactSelectOptions(children);
  var [valueState, setValue] = React.useState(() => {
    if (defaultValue) {
      return getValue(options, defaultValue);
    }
  });
  var [isControlled, value] = useControllableProp(getValue(options, valueProp), valueState);
  var handleOnChange = React.useCallback(selectedValue => {
    if (!isControlled) {
      setValue(selectedValue);
    }
    onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(selectedValue.value);
  }, [onChangeProp, isControlled]);

  /**
   * React-Select has an open issue where the current selected option
   * won't be scrolled to when you first open a Select component when
   * it is used within a custom component. This fixes it by
   * manually getting the selected option if there is one and then
   * setting it to the focused option.
   * @see https://github.com/JedWatson/react-select/issues/3648
   */
  var onMenuOpen = () => {
    requestAnimationFrame(() => {
      var _reactSelectComponent, _reactSelectComponent2, _reactSelectComponent3;
      if (!reactSelectComponentRef.current) return;
      var option = (_reactSelectComponent = reactSelectComponentRef.current) === null || _reactSelectComponent === void 0 ? void 0 : (_reactSelectComponent2 = _reactSelectComponent.state) === null || _reactSelectComponent2 === void 0 ? void 0 : (_reactSelectComponent3 = _reactSelectComponent2.selectValue) === null || _reactSelectComponent3 === void 0 ? void 0 : _reactSelectComponent3[0];
      if (!option) return;
      var menuRef = reactSelectComponentRef.current.menuListRef;
      var selectedOption = menuRef === null || menuRef === void 0 ? void 0 : menuRef.querySelector(".".concat(SELECT_CLASSNAME_PREFIX, "__option--is-selected"));
      if (selectedOption) {
        reactSelectComponentRef.current.setState({
          focusedValue: null,
          focusedOption: option
        });
        selectedOption.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'start'
        });
      }
      onMenuOpenProp();
    });
  };

  /**
   * React-Select returns a StateManger object from
   * its ref attribute instead of the DOM element of
   * the component. StateManager object contains the
   * DOM element ref so we have to manually assign it
   * when the component is first initialized.
   */
  useEffect(() => {
    if (reactSelectComponentRef.current) {
      assignRef(ref, reactSelectComponentRef.current.inputRef);
    }
  }, [reactSelectComponentRef, ref]);
  var addDataAttributes = (Component, _dataAttributes) => optionProps => {
    if (_dataAttributes) {
      /**
       * Sanitising dataAttributes
       */
      var filtered = Object.keys(_dataAttributes).filter(key => key.startsWith('data-')).reduce((obj, key) => {
        return _objectSpread(_objectSpread({}, obj), {}, {
          [key]: _dataAttributes[key]
        });
      }, {});
      return /*#__PURE__*/React.createElement(Component, _extends({}, optionProps, {
        innerProps: Object.assign({}, optionProps.innerProps, _objectSpread({}, filtered))
      }));
    } else {
      return /*#__PURE__*/React.createElement(Component, optionProps);
    }
  };
  var TextOption = optionProps => components.Option && /*#__PURE__*/React.createElement(components.Option, optionProps, optionProps.children);
  var getRef = () => {
    if (!stateManagerRef) return reactSelectComponentRef;
    return mergeRefs(reactSelectComponentRef, stateManagerRef);
  };
  return /*#__PURE__*/React.createElement(tqc.div, _extends({
    __css: styles.container,
    sx: sx
  }, layoutProps, {
    "data-testid": otherProps['data-testid'] ? otherProps['data-testid'] : null
  }), /*#__PURE__*/React.createElement(ReactSelect, _extends({
    ref: getRef(),
    options: options,
    value: value,
    name: name,
    onChange: newValue => handleOnChange(newValue),
    className: cx(SELECT_CLASSNAME_PREFIX, selectElementProps['aria-invalid'] ? "".concat(SELECT_CLASSNAME_PREFIX, "--is-invalid") : '', className),
    classNamePrefix: SELECT_CLASSNAME_PREFIX,
    menuPortalTarget: typeof document === 'undefined' ? null : document.body,
    styles: {
      menuPortal: base => _objectSpread(_objectSpread({}, base), {}, {
        zIndex: menuZIndex !== null && menuZIndex !== void 0 ? menuZIndex : 9999
      })
    },
    isDisabled: selectElementProps.disabled,
    "aria-labelledby": props['aria-labelledby'],
    id: props.id,
    onMenuOpen: onMenuOpen,
    components: {
      Menu: function Menu(menuProps) {
        var {
          Menu: MenuComponent
        } = components;
        return /*#__PURE__*/React.createElement(tqc.div, {
          __css: styles.menu
        }, /*#__PURE__*/React.createElement(MenuComponent, menuProps));
      },
      DropdownIndicator: function DropdownIndicator() {
        return /*#__PURE__*/React.createElement(SelectIcon, _extends({}, iconSize && {
          fontSize: iconSize
        }, iconColor && {
          color: iconColor
        }), icon);
      },
      Option: addDataAttributes(TextOption, dataAttributes)
    }
  }, otherProps)));
});
if (__DEV__) {
  Select.displayName = 'Select';
}
export var DefaultIcon = props => /*#__PURE__*/React.createElement("svg", _extends({
  viewBox: "0 0 24 24"
}, props), /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z"
}));
var IconWrapper = tqc('div', {
  baseStyle: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none'
  }
});
var SelectIcon = props => {
  var {
      children = /*#__PURE__*/React.createElement(DefaultIcon, null)
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var clone = /*#__PURE__*/React.cloneElement(children, {
    role: 'img',
    className: "".concat(SELECT_CLASSNAME_PREFIX, "__icon"),
    focusable: false,
    'aria-label': 'View options',
    // force icon to adhere to `IconWrapper` styles
    style: {
      width: '1em',
      height: '1em',
      color: 'currentColor'
    }
  });
  return /*#__PURE__*/React.createElement(IconWrapper, _extends({}, rest, {
    className: "".concat(SELECT_CLASSNAME_PREFIX, "__icon-wrapper")
  }), /*#__PURE__*/React.isValidElement(children) ? clone : null);
};
if (__DEV__) {
  SelectIcon.displayName = 'SelectIcon';
}